import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import theme from './theme';
import Homepage from './components/Homepage';
import Footer from './components/pages/Footer';
import AllTrips from './components/AllTrips';
import Navbar from './components/Navbar';
import Build from './components/Build';
import Login from './components/pages/Login';
import Signup from './components/pages/Signup';
import ContactForm from './components/pages/ContactForm';
import DetailedTrip from './components/pages/DetailedTrip';
import TripBuilder from './components/pages/TripBuilder';
import About from './components/About';
import BookTrip from './components/pages/BookTrip';
import SharedPlan from './components/pages/SharedPlan';
import Gallery from './components/pages/Gallery';
import ScrollToTop from './components/ScrollToTop';
import ImpersonationBanner from './components/admin/ImpersonationBanner';

// Admin Dashboard Components
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/Dashboard';
import TripsManagement from './components/admin/TripsManagement';
import BookingsManagement from './components/admin/BookingsManagement';
import PaymentsManagement from './components/admin/PaymentsManagement';
import ReviewsManagement from './components/admin/ReviewsManagement';
import ContactManagement from './components/admin/ContactManagement';
import NewsletterManagement from './components/admin/NewsletterManagement';
import UsersManagement from './components/admin/UsersManagement';
import PlanRequestsManagement from './components/admin/PlanRequestsManagement';
import AnalyticsPage from './components/admin/AnalyticsPage';
import BEUX from './components/admin/BE-UX';
import AdminProfile from './components/admin/AdminProfile';
import AdminSettings from './components/admin/AdminSettings';
import AuditLog from './components/admin/AuditLog';
import SystemStatus from './components/admin/SystemStatus';
import Broadcast from './components/admin/Broadcast';

// User Dashboard Components
import UserLayout from './components/user/UserLayout';
import UserDashboard from './components/user/UserDashboard';
import UserProfile from './components/user/UserProfile';
import MyBookings from './components/user/MyBookings';
import MyFavorites from './components/user/MyFavorites';
import MyPlans from './components/user/MyPlans';
import MyRequests from './components/user/MyRequests';
import CreatePlan from './components/pages/CreatePlan';
import UserSettings from './components/user/UserSettings';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import TermsOfService from './components/pages/TermsOfService';
import NotFound from './components/pages/NotFound';
import ProtectedRoute from './components/routing/ProtectedRoute';
import { AuthModalProvider } from './context/AuthModalContext';

function App() {
  const location = useLocation();
  
  // Check if current route is a dashboard route
  const isDashboardRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/user');
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline /> {/* Provides a consistent baseline CSS */}
      <AuthModalProvider>
      <div className="flex flex-col min-h-screen">
        <ScrollToTop />
        <ImpersonationBanner />
        {!isDashboardRoute && <Navbar />}
        <Box 
          component="main"
          className="flex-grow" 
          sx={{ 
            paddingTop: isDashboardRoute ? 0 : { xs: 7, sm: 8, md: 9 },
            minHeight: 'calc(100vh - 64px)'
          }}
        > 
          <Routes>
            {/* ===== Marketing / public zone (default landing) ===== */}
            <Route path="/" element={<Homepage />} />
            <Route path="/trips" element={<AllTrips />} />
            <Route path='/trips/:id' element={<DetailedTrip />} />
            <Route path="/about" element={<About />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/contact" element={<ContactForm />} />
            <Route path="/book" element={<BookTrip />} />
            <Route path="/p/:slug" element={<SharedPlan />} />
            <Route path="/construction" element={<Build />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />

            {/* Trip builder is part of the dynamic app: requires auth. */}
            <Route
              path='/builder/:id'
              element={
                <ProtectedRoute requireAuth>
                  <TripBuilder />
                </ProtectedRoute>
              }
            />

            {/* ===== Admin zone (role: admin) ===== */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAuth requiredRole="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="trips" element={<TripsManagement />} />
              <Route path="bookings" element={<BookingsManagement />} />
              <Route path="plan-requests" element={<PlanRequestsManagement />} />
              <Route path="payments" element={<PaymentsManagement />} />
              <Route path="reviews" element={<ReviewsManagement />} />
              <Route path="contacts" element={<ContactManagement />} />
              <Route path="newsletter" element={<NewsletterManagement />} />
              <Route path="users" element={<UsersManagement />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="be-ux" element={<BEUX />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="system" element={<SystemStatus />} />
              <Route path="broadcast" element={<Broadcast />} />
              <Route path="profile" element={<AdminProfile />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
            
            {/* ===== App / dynamic zone (authenticated) — kept at /user/* (smallest diff) ===== */}
            <Route
              path="/user"
              element={
                <ProtectedRoute requireAuth>
                  <UserLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<UserDashboard />} />
              <Route path="dashboard" element={<UserDashboard />} />
              <Route path="profile" element={<UserProfile />} />
              <Route path="bookings" element={<MyBookings />} />
              <Route path="plans" element={<MyPlans />} />
              <Route path="requests" element={<MyRequests />} />
              <Route path="create-plan" element={<CreatePlan />} />
              <Route path="favorites" element={<MyFavorites />} />
              <Route path="settings" element={<UserSettings />} />
            </Route>
            
            {/* Real 404 (marketing chrome), not a silent redirect to Homepage. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>
        {!isDashboardRoute && <Footer />}
      </div>
      </AuthModalProvider>
    </ThemeProvider>
  );
}

export default App;
